/**
 *  NAPLÓ-NYOMTATÁS – önálló nyomtatási dokumentum egy rácsból.
 *
 *  A képernyőt nem nyomtatjuk (`window.print()` az oldalon): az Ionic tartalom egy görgetett
 *  doboz, amiből a böngésző csak a látható darabot, a fejléccel és a menüvel együtt tette
 *  papírra – egy többoldalas napló így használhatatlan volt. Helyette egy rejtett keretben
 *  (iframe) tiszta HTML-táblát építünk és azt nyomtatjuk:
 *
 *    - fekvő A4, a táblázat fejléce MINDEN oldalon megismétlődik, sor nem törik ketté;
 *    - a lap tetején az ügyfél (név, adószám, cím), a napló címe, az év és MINDEN érvényes
 *      szűrés (időszak, forrás, állapot, keresés) – papíron is látszódjon, mi van rajta;
 *    - a végén az összesítő sor, alul a nyomtatás ideje és az oldalszám;
 *    - a teljesen üres oszlopok (pl. deviza, ha nincs devizás tétel) kimaradnak – a lap alján
 *      felsoroljuk, melyek –, sok oszlopnál kisebb a betű, és ha a tábla így sem fér el a lap
 *      szélességében, a nyomtatás előtt arányosan kicsinyítjük (semmi nem lóghat le a lapról).
 *
 *  A színek a képernyő oszlopcsoport-színeinek halvány, fekete-fehérben is olvasható párjai.
 */

import { ablakNyomtat } from '../nyomtatas-elonezet';

export interface NyomtatasOszlop { k: string; c: string; num?: boolean; cs?: string; szeles?: boolean; }

export interface NyomtatasAdat {
  cim: string;                                   // pl. „Pénzügyi nyilvántartás"
  ugyfel: { nev?: string | null; adoszam?: string | null; cim?: string | null };
  ev?: number | string;
  mod?: string;                                  // „Egyszeres könyvelés" / „Kettős könyvelés"
  szurok: [string, string][];                    // [címke, érték] – csak a kitöltöttek
  oszlopok: NyomtatasOszlop[];
  sorok: any[];
  ertek: (sor: any, o: NyomtatasOszlop) => string;
  osszegSor?: (o: NyomtatasOszlop, i: number) => string;   // az összesítő sor cellái ('' = üres)
  sorOsztaly?: (sor: any) => string;             // pl. 'gyujto' (kiemelt sor)
  keszitette?: string;
  /** A teljesen üres oszlopok elhagyása (alapból igen). */
  uresOszlopNelkul?: boolean;
}

const esc = (v: any) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** CSS-karakterlánchoz (a <style> nyers szöveg: ott a HTML-entitás nem oldódna fel). */
const cssStr = (v: any) => String(v ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/</g, '\\3C ').replace(/[\r\n]+/g, ' ');

export function naploNyomtatasHtml(bemenet: NyomtatasAdat): string {
  // Üres oszlop: egyik sorban sincs értéke, és az összesítőben sem áll semmi.
  const elhagyott: string[] = [];
  const oszlopok = bemenet.uresOszlopNelkul === false ? bemenet.oszlopok : bemenet.oszlopok.filter((o, i) => {
    const van = bemenet.sorok.some((r) => bemenet.ertek(r, o) !== '')
      || (!!bemenet.osszegSor && i > 0 && !/^[0\s]*$/.test(bemenet.osszegSor(o, i).replace(/[−-]/g, '')));
    if (!van && i > 0) elhagyott.push(o.c);
    return van || i === 0;
  });
  const a: NyomtatasAdat = { ...bemenet, oszlopok,
    osszegSor: bemenet.osszegSor ? (o, i) => (i === 0 ? 'Összesen' : bemenet.osszegSor!(o, bemenet.oszlopok.indexOf(o))) : undefined };
  const most = new Date();
  const ido = most.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  const sok = a.oszlopok.length > 13;
  const fej = a.oszlopok.map((o) => `<th class="${o.num ? 'num ' : ''}${o.cs || ''}">${esc(o.c)}</th>`).join('');
  const sorok = a.sorok.map((r) => {
    const cellak = a.oszlopok.map((o) => {
      const v = a.ertek(r, o);
      const neg = o.num && /^[−-]/.test(v);
      return `<td class="${o.num ? 'num ' : ''}${o.szeles ? 'szeles ' : ''}${neg ? 'neg ' : ''}${o.cs || ''}">${esc(v)}</td>`;
    }).join('');
    return `<tr class="${esc(a.sorOsztaly?.(r) || '')}">${cellak}</tr>`;
  }).join('');
  const ossz = a.osszegSor
    ? `<tr class="osszesen">${a.oszlopok.map((o, i) => `<td class="${o.num ? 'num ' : ''}${o.cs || ''}">${esc(a.osszegSor!(o, i))}</td>`).join('')}</tr>`
    : '';
  const ugyfelSor = [a.ugyfel.adoszam ? `Adószám: ${esc(a.ugyfel.adoszam)}` : '', a.ugyfel.cim ? esc(a.ugyfel.cim) : '']
    .filter(Boolean).join(' · ');
  const szurok = (a.szurok.length
    ? a.szurok.map(([k, v]) => `<span><b>${esc(k)}:</b> ${esc(v)}</span>`).join('')
    : '<span>Szűrés nélkül – az év összes tétele</span>')
    + (elhagyott.length ? `<span class="elhagyott"><b>Üres oszlopok (nincsenek a lapon):</b> ${esc(elhagyott.join(', '))}</span>` : '');

  return `<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>${esc(a.cim)} – ${esc(a.ugyfel.nev || '')}</title>
<style>
  @page { size: A4 landscape; margin: 11mm 9mm 13mm;
    @bottom-left { content: "${cssStr(a.cim)} · ${cssStr(a.ugyfel.nev || '')}"; font: 7pt Arial, sans-serif; color: #666; }
    @bottom-right { content: counter(page) ". / " counter(pages) ". oldal"; font: 7pt Arial, sans-serif; color: #666; } }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font: ${sok ? '6.8pt' : '8pt'}/1.3 Arial, "Helvetica Neue", sans-serif; color: #111; }
  .fej { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px;
    border-bottom: 1.5pt solid #111; padding-bottom: 5pt; margin-bottom: 5pt; }
  .fej h1 { margin: 0; font-size: 14pt; letter-spacing: .01em; }
  .fej .ugyfel { font-size: 10pt; font-weight: 700; margin-top: 2pt; }
  .fej .ugyfel-adat { font-size: 8pt; color: #333; margin-top: 1pt; }
  .fej .jobb { text-align: right; font-size: 8pt; color: #333; line-height: 1.45; }
  .fej .jobb b { font-size: 11pt; color: #111; }
  .szurok { display: flex; flex-wrap: wrap; gap: 3pt 14pt; font-size: 7.5pt; color: #333; margin: 0 0 6pt; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  th { background: #e9ecef; border-top: .8pt solid #111; border-bottom: .8pt solid #111; font-weight: 700;
    text-align: left; padding: 3pt ${sok ? '3pt' : '4pt'}; vertical-align: bottom; font-size: ${sok ? '6.2pt' : '7.4pt'};
    white-space: normal; min-width: 9mm; }
  td { padding: 2.2pt ${sok ? '3pt' : '4pt'}; border-bottom: .4pt solid #cfd4d9; white-space: nowrap; vertical-align: top; }
  td.szeles { white-space: normal; min-width: ${sok ? '24mm' : '34mm'}; max-width: ${sok ? '46mm' : '70mm'}; overflow-wrap: break-word; }
  .szurok .elhagyott { color: #666; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  td.neg { color: #111; }
  tbody tr:nth-child(even) td { background: #f7f8f9; }
  th.cs-penz { background: #dcebe0; } td.cs-penz { background: #f1f7f2 !important; }
  th.cs-afa { background: #d9e8f3; } td.cs-afa { background: #eef5fa !important; }
  th.cs-deviza { background: #f2e7bf; } td.cs-deviza { background: #fbf6e4 !important; }
  tr.gyujto td { font-weight: 700; background: #eceff2 !important; }
  tr.osszesen td { font-weight: 700; border-top: 1.2pt solid #111; border-bottom: 1.2pt solid #111; background: #e9ecef !important; }
  .lab { margin-top: 8pt; font-size: 7pt; color: #555; display: flex; justify-content: space-between; }
  .ures { padding: 20pt; text-align: center; color: #555; border: .6pt dashed #999; }
</style></head><body>
  <div class="fej">
    <div>
      <h1>${esc(a.cim)}</h1>
      <div class="ugyfel">${esc(a.ugyfel.nev || '')}</div>
      ${ugyfelSor ? `<div class="ugyfel-adat">${ugyfelSor}</div>` : ''}
    </div>
    <div class="jobb">
      ${a.ev ? `<b>${esc(a.ev)}. év</b><br>` : ''}
      ${a.mod ? `${esc(a.mod)}<br>` : ''}
      ${a.sorok.length} tétel
    </div>
  </div>
  <div class="szurok">${szurok}</div>
  ${a.sorok.length
    ? `<table id="racs"><thead><tr>${fej}</tr></thead><tbody>${sorok}${ossz}</tbody></table>`
    : '<div class="ures">Nincs a szűrésnek megfelelő tétel.</div>'}
  <div class="lab"><span>Nyomtatva: ${esc(ido)}${a.keszitette ? ' · ' + esc(a.keszitette) : ''}</span><span>Cronomit · Könyvelés</span></div>
</body></html>`;
}

/** A nyomtatható szélesség képpontban (fekvő A4 − 2 × 9 mm margó, 96 dpi). */
const LAP_PX = Math.floor((297 - 18) / 25.4 * 96);

/** Ha a tábla szélesebb a lapnál, az egész lapot arányosan kicsinyítjük (nem vágjuk le a szélét). */
export function illesztSzelesseg(doc: Document): number {
  const t = doc.getElementById('racs');
  if (!t) return 1;
  const arany = LAP_PX / Math.max(t.scrollWidth, 1);
  if (arany < 1) (doc.body.style as any).zoom = String(Math.max(arany, 0.5));
  return arany;
}

/** Nyomtatás rejtett keretből: a képernyő nem változik, a böngésző nyomtatási ablaka nyílik. */
export function naploNyomtat(a: NyomtatasAdat): void {
  const keret = document.createElement('iframe');
  keret.setAttribute('aria-hidden', 'true');
  // A keret a nyomtatható lapszélességű (fekvő A4, margók nélkül ≈ 279 mm), hogy a tábla
  // szélességét mérni lehessen – láthatatlan, a képernyőn semmit nem takar.
  keret.style.cssText = `position:fixed;left:-10000px;top:0;width:${LAP_PX}px;height:800px;border:0;visibility:hidden`;
  document.body.appendChild(keret);
  const doc = keret.contentDocument!;
  doc.open();
  doc.write(naploNyomtatasHtml(a));
  doc.close();
  const takarit = () => setTimeout(() => keret.remove(), 1000);
  keret.contentWindow!.addEventListener('afterprint', takarit);
  // A betűk és a táblázat elrendezése után nyomtassunk (különben üres első oldal lehet).
  setTimeout(() => {
    try {
      illesztSzelesseg(doc);
      ablakNyomtat(keret.contentWindow!, a.cim);
    } finally { setTimeout(takarit, 60000); }
  }, 250);
}
